import { LocationSearch } from '@griply/hooks';
import {
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventGuestNotification,
  CalendarEventOccurrenceTarget,
  CalendarEventResponseStatus,
  CalendarEventWarning,
  ConnectionCalendar,
  ExternalCalendarEventPreview,
  ExternalConnection,
  ExternalConnectionContact,
} from '@griply/types';
import {
  getCalendarEventRowState,
  getCalendarEventVideoCall,
  getConnectionProvider,
  getExternalEventEditable,
  getProviderRecurrenceLimits,
  hasCalendarChanged,
  isCalendarEventFormValid,
  mapEventDetailsToFormState,
  shouldAskGuestNotification,
} from '@griply/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Sizes } from 'shared/components/ui/button';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import { EventApplyToOccurrencesDialog } from './event-apply-to-occurrences-dialog';
import { EventForm } from './event-form';
import * as Styled from './event-form.style';
import * as PopupStyled from './event-form-popup.style';
import { EventFormError, EventFormGone } from './event-form-status';
import { EventNotifyGuestsDialog } from './event-notify-guests-dialog';
import { EventReadOnlyView } from './event-read-only-view';

const POPUP_WIDTH = 320;

export type EventFormPopupProps = {
  anchor: { top: number; left: number };
  // the instantly-available fields from the clicked grid event (or the create slot)
  seed: CalendarEventFormFields;
  // undefined while the detail fetch is in flight; null when the event no longer exists
  details?: CalendarEventDetails | null;
  // what the events list already answered, so those rows never skeleton
  preview?: ExternalCalendarEventPreview;
  isLoadingDetails?: boolean;
  isLoadingCalendars?: boolean;
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  // only to resolve the target calendar's provider — the recurrence builder narrows its options by it
  connections: ExternalConnection[];
  contacts?: ExternalConnectionContact[];
  locationSearch: LocationSearch;
  // the zone the event's own calendar runs on; undefined until the calendars resolve
  calendarTimeZone?: string | null;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  isEdit: boolean;
  isVideoCallLocked?: boolean;
  canReplaceVideoCall?: boolean;
  isSaving?: boolean;
  isError?: boolean;
  // the refusal the provider gave, already translated by the caller
  errorDescription?: string;
  warnings?: CalendarEventWarning[] | null;
  isResponding?: boolean;
  hasRespondError?: boolean;
  // where an invitee goes to counter-propose a time — the provider's own screen
  proposeTimeUrl?: string;
  // without premium every event opens read-only, whoever organises it
  canEditEvents?: boolean;
  // the event at the provider, offered next to the upgrade prompt
  eventUrl?: string;
  onSubmit: (
    fields: CalendarEventFormFields,
    occurrenceTarget: CalendarEventOccurrenceTarget,
    guestNotification: CalendarEventGuestNotification | null,
  ) => void;
  onRespond: (status: CalendarEventResponseStatus) => void;
  onUnlockEdit: () => void;
  // header actions; the popup hands duplicate the fields as they stand on screen
  onDuplicate: (fields: CalendarEventFormFields) => void;
  onDelete: () => void;
  onRetry: () => void;
  onClose: () => void;
};

export const EventFormPopup: React.FC<EventFormPopupProps> = ({
  anchor,
  seed,
  details,
  preview,
  isLoadingDetails,
  isLoadingCalendars,
  calendars,
  selectableCalendars,
  connections,
  contacts,
  locationSearch,
  calendarTimeZone,
  dateFormat,
  weekStartsOn,
  timeFormat,
  isEdit,
  isVideoCallLocked,
  canReplaceVideoCall,
  isSaving,
  isError,
  errorDescription,
  warnings,
  isResponding,
  hasRespondError,
  proposeTimeUrl,
  canEditEvents,
  eventUrl,
  onSubmit,
  onRespond,
  onUnlockEdit,
  onDuplicate,
  onDelete,
  onRetry,
  onClose,
}) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [occurrencesPromptOpen, setOccurrencesPromptOpen] = useState(false);
  // holds the answered occurrence target while the guest question is on screen
  const [notifyPromptTarget, setNotifyPromptTarget] =
    useState<CalendarEventOccurrenceTarget | null>(null);
  // set when the user leaves the popup before the detail fetch landed — see onOutside
  const [commitPending, setCommitPending] = useState(false);

  // Only what the user actually touched. The base underneath keeps re-deriving as the
  // detail fetch (and the calendars fetch, for create) resolves, so late-arriving fields
  // fill in without ever overwriting an edit in progress.
  const [edits, setEdits] = useState<Partial<CalendarEventFormFields>>({});

  const base = useMemo(
    () =>
      details ? mapEventDetailsToFormState(details, calendarTimeZone) : seed,
    [calendarTimeZone, details, seed],
  );
  const fields = useMemo(() => ({ ...base, ...edits }), [base, edits]);

  const onChangeFields = (partial: Partial<CalendarEventFormFields>) =>
    setEdits((previous) => ({ ...previous, ...partial }));

  const isDirty = !!Object.keys(edits).length;

  const isValid = useMemo(() => isCalendarEventFormValid(fields), [fields]);

  const rowState = useMemo(
    () => getCalendarEventRowState({ isLoadingDetails, preview }),
    [isLoadingDetails, preview],
  );

  const videoCall = useMemo(
    () => getCalendarEventVideoCall(details, preview, t),
    [details, preview, t],
  );

  // Follows the picked calendar, not the event's original one: switching an event onto an Outlook
  // calendar has to narrow the builder before the user writes a rule that calendar cannot hold.
  const provider = useMemo(
    () =>
      fields.calendar
        ? getConnectionProvider(connections, fields.calendar.connectionId)
        : null,
    [connections, fields.calendar],
  );

  const recurrenceLimits = useMemo(
    () => getProviderRecurrenceLimits(provider),
    [provider],
  );

  const hasWarnings = !!warnings?.length;
  const isGone = isEdit && details === null;
  const isEditLocked = isEdit && canEditEvents === false;
  // Same gate mobile's detail screen applies — an invitee OR a read-only connection — and
  // without premium nobody can edit; all three land read-only.
  const isReadOnly =
    isEditLocked ||
    (isEdit &&
      !!fields.calendar &&
      !getExternalEventEditable(fields.calendar, {
        connections,
        isOrganizer: details?.isOrganizer,
      }));

  const openOccurrencesPrompt = () => setOccurrencesPromptOpen(true);
  const closeOccurrencesPrompt = () => setOccurrencesPromptOpen(false);

  // Only Google can suppress the mail, and only an event with someone else on it has a reason to
  const asksAboutGuests = shouldAskGuestNotification({
    provider: details
      ? getConnectionProvider(connections, details.connectionId) ?? undefined
      : undefined,
    guests: details?.guests ?? [],
    organizer: details?.organizer,
  });

  const submitWithTarget = (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => {
    if (asksAboutGuests) {
      setNotifyPromptTarget(occurrenceTarget);
      return;
    }
    onSubmit(fields, occurrenceTarget, null);
  };

  const submitForOccurrences = (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => {
    closeOccurrencesPrompt();
    submitWithTarget(occurrenceTarget);
  };

  const submitForNotify = (
    guestNotification: CalendarEventGuestNotification,
  ) => {
    if (!notifyPromptTarget) {
      return;
    }
    const occurrenceTarget = notifyPromptTarget;
    setNotifyPromptTarget(null);
    onSubmit(fields, occurrenceTarget, guestNotification);
  };

  // Discards, for the same reason the occurrence prompt below does
  const cancelNotifyPrompt = () => {
    setNotifyPromptTarget(null);
    onClose();
  };

  // Cancelling the recurrence question abandons the edit outright: the prompt is only
  // reached by leaving the popup, so returning to it with the edits still staged would
  // re-open this same dialog on the next outside click with no way out. Closing discards —
  // `edits` lives only here, and nothing was written.
  const cancelOccurrencesPrompt = () => {
    closeOccurrencesPrompt();
    onClose();
  };

  // both leave the popup, so neither can be caught by the leave-to-commit path below
  const onSelectDuplicate = () => onDuplicate(fields);
  const onSelectDelete = () => onDelete();

  // Dismissed before acting — a layered dialog would leave Escape closing the popup instead.
  const onSelectUnlockEdit = () => {
    onClose();
    onUnlockEdit();
  };

  const onSelectOpenInCalendar = () => {
    onClose();
    window.open(eventUrl, '_blank', 'noreferrer');
  };

  const commit = (loadedDetails: CalendarEventDetails | null | undefined) => {
    if (isEdit && loadedDetails?.isRecurring) {
      // A move can only take the WHOLE series, so the occurrence question would offer a refusable answer.
      if (hasCalendarChanged(fields.calendar, loadedDetails)) {
        submitWithTarget(CalendarEventOccurrenceTarget.Series);
        return;
      }
      openOccurrencesPrompt();
      return;
    }
    submitWithTarget(CalendarEventOccurrenceTarget.Single);
  };

  // There is no Save button: leaving the popup is the commit. An untouched, invalid or
  // status-screen popup just closes — only a valid edit writes, and a recurring one asks
  // which occurrences it applies to first.
  const onOutside = () => {
    if (
      occurrencesPromptOpen ||
      notifyPromptTarget ||
      isSaving ||
      commitPending
    ) {
      return;
    }
    if (
      isError ||
      hasWarnings ||
      isGone ||
      isReadOnly ||
      !isDirty ||
      !isValid
    ) {
      onClose();
      return;
    }
    // An edit patch is built from the full detail record. Committing on the seed alone
    // would send empty guests/description/reminders and wipe them at the provider — so
    // hold the popup open and write as soon as the fetch lands.
    if (isEdit && !details) {
      setCommitPending(true);
      return;
    }
    commit(details);
  };

  const content = isError ? (
    <EventFormError
      description={errorDescription}
      onRetry={onRetry}
      onCancel={onClose}
    />
  ) : isGone ? (
    <EventFormGone onClose={onClose} />
  ) : isReadOnly ? (
    <EventReadOnlyView
      fields={fields}
      timeFormat={timeFormat}
      weekStartsOn={weekStartsOn}
      isLoadingDetails={isLoadingDetails}
      rowState={rowState}
      isRecurring={details?.isRecurring}
      seriesRrule={details?.seriesRrule}
      videoCallUrl={videoCall.url}
      videoCallLabel={videoCall.label}
      descriptionHtml={details?.description}
      organizer={details?.organizer}
      responseStatus={details?.selfResponseStatus}
      isResponding={isResponding}
      hasRespondError={hasRespondError}
      proposeTimeUrl={proposeTimeUrl}
      isEditLocked={isEditLocked}
      onRespond={onRespond}
      onOpenInCalendar={eventUrl ? onSelectOpenInCalendar : undefined}
      onUnlockEdit={onSelectUnlockEdit}
    />
  ) : (
    <EventForm
      fields={fields}
      onChangeFields={onChangeFields}
      calendars={calendars}
      selectableCalendars={selectableCalendars}
      contacts={contacts}
      locationSearch={locationSearch}
      recurrenceLimits={recurrenceLimits}
      provider={provider}
      dateFormat={dateFormat}
      weekStartsOn={weekStartsOn}
      timeFormat={timeFormat}
      isLoadingDetails={isLoadingDetails}
      isLoadingCalendars={isLoadingCalendars}
      rowState={rowState}
      isRecurring={details?.isRecurring}
      seriesRrule={details?.seriesRrule}
      isVideoCallLocked={isVideoCallLocked}
      videoCallUrl={videoCall.url}
      videoCallLabel={videoCall.label}
      canReplaceVideoCall={canReplaceVideoCall}
      descriptionHtml={details?.description}
      titleRef={titleRef}
      onDuplicate={isEdit ? onSelectDuplicate : undefined}
      onDelete={isEdit ? onSelectDelete : undefined}
    />
  );

  useEffect(() => {
    if (!commitPending || details === undefined) {
      return;
    }
    setCommitPending(false);
    details === null ? onClose() : commit(details);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once the awaited details resolve
  }, [commitPending, details]);

  return (
    <PopupMenu
      location={anchor}
      position="right-start"
      onOutside={onOutside}
      width={POPUP_WIDTH}
      // a new event opens ready to type its title; an existing one must not have its
      // title text put under a caret just for being opened
      initialFocus={isEdit ? undefined : titleRef}
      focusOnOpen
    >
      <PopupStyled.Container $saving={isSaving || commitPending}>
        {content}

        {hasWarnings && (
          <>
            <Styled.WarningBanner role="status">
              {warnings!.map((warning) => (
                <span key={warning.code}>
                  {t(`forms.event.warnings.${warning.code}`, {
                    defaultValue: warning.message,
                  })}
                </span>
              ))}
            </Styled.WarningBanner>
            <PopupStyled.Interactions>
              <Button size={Sizes.Medium} onClick={onClose} type="button">
                {t('forms.event.buttons.done.label')}
              </Button>
            </PopupStyled.Interactions>
          </>
        )}
      </PopupStyled.Container>

      {occurrencesPromptOpen && (
        <EventApplyToOccurrencesDialog
          onSelect={submitForOccurrences}
          onClose={cancelOccurrencesPrompt}
        />
      )}

      {!!notifyPromptTarget && (
        <EventNotifyGuestsDialog
          onSelect={submitForNotify}
          onClose={cancelNotifyPrompt}
        />
      )}
    </PopupMenu>
  );
};
